/**
 * Backlog #15: unified calendar. Recurrence expansion is done in local wall-clock time (Europe/London by
 * default) and only then converted to instants, so a weekly 09:00 meeting stays at 09:00 across the
 * BST/GMT changes. Shared by the API (layers, reminders, iCal) and the web client.
 */
import { DateTime } from 'luxon';

export const CALENDAR_TZ = 'Europe/London';
export type CalendarScope = 'personal' | 'company' | 'team' | 'building';
export type CalendarLayer = 'company' | 'team' | 'building' | 'personal' | 'birthdays' | 'tasks' | 'absences' | 'closures' | 'shifts' | 'todos';
export const CALENDAR_LAYERS: CalendarLayer[] = ['company', 'team', 'building', 'personal', 'birthdays', 'tasks', 'absences', 'closures', 'shifts', 'todos'];
export const CALENDAR_LAYER_LABELS: Record<CalendarLayer, string> = {
  company: 'Company events', team: 'Team events', building: 'Building / site events', personal: 'My events',
  birthdays: 'Birthdays', tasks: 'Task due dates', absences: 'Absences', closures: 'Closures & bank holidays', shifts: 'My shifts', todos: 'My private tasks (only you)',
};
/** Layer colours. Used as chip fills under white text, so each must reach WCAG AA (4.5:1) against white
 *  (checked in apps/web/src/lib/brandContrast.test.ts). Building events are orange, not amber, so they
 *  never read as the brand yellow used for the today marker. */
export const CALENDAR_LAYER_COLORS: Record<CalendarLayer, string> = {
  company: '#4f46e5', team: '#0e7490', building: '#c2410c', personal: '#7c3aed',
  birthdays: '#be185d', tasks: '#2563eb', absences: '#64748b', closures: '#dc2626', shifts: '#047857', todos: '#4d7c0f',
};
export type TaskScope = 'mine' | 'teams' | 'all';
export type Rsvp = 'pending' | 'accepted' | 'tentative' | 'declined';
export type EditMode = 'all' | 'this' | 'following';

export interface CalendarRecurrence {
  freq: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval?: number;
  /** weekly: ISO weekdays 1 (Mon) .. 7 (Sun); defaults to the start's weekday */
  byweekday?: number[];
  /** last date (inclusive, local) */
  until?: string | null;
  /** total occurrences */
  count?: number | null;
}

/** Local wall-clock "YYYY-MM-DDTHH:mm" (timed) - for all-day events "YYYY-MM-DDT00:00" with an exclusive end. */
export interface SeriesTiming {
  starts_local: string;
  ends_local: string;
  all_day: boolean;
  recurrence: CalendarRecurrence | null;
  timezone?: string;
}

export interface OccurrenceOverride {
  /** local start of the occurrence this replaces */
  occurrence: string;
  cancelled?: boolean;
  starts_local?: string;
  ends_local?: string;
  title?: string;
  description?: string | null;
  location_text?: string | null;
}

export interface Occurrence {
  /** stable id of the occurrence within the series: its original local start */
  key: string;
  starts_local: string;
  ends_local: string;
  /** instants (ISO, UTC) */
  start: string;
  end: string;
  override?: OccurrenceOverride;
}

export const CALENDAR_LIMITS = { title: 200, description: 4000, location: 200, reminders: 5, reminderMaxMinutes: 7 * 24 * 60, rangeDays: 120, occurrences: 1000 } as const;

const LOCAL_FMT = "yyyy-LL-dd'T'HH:mm";
export const parseLocal = (s: string, zone = CALENDAR_TZ) => DateTime.fromISO(s.length === 10 ? `${s}T00:00` : s, { zone });
export const fmtLocal = (d: DateTime) => d.toFormat(LOCAL_FMT);

/** Wall-clock difference in minutes (ignores DST so durations stay in local terms). */
function wallMinutes(a: string, b: string) {
  const x = DateTime.fromISO(a, { zone: 'UTC' }); const y = DateTime.fromISO(b, { zone: 'UTC' });
  return Math.round(y.diff(x, 'minutes').minutes);
}
const addWall = (local: string, minutes: number) => fmtLocal(DateTime.fromISO(local, { zone: 'UTC' }).plus({ minutes }));

/** Local start strings of every occurrence (before overrides), in order, stopping after `untilLocal`. */
export function* seriesStarts(t: SeriesTiming, untilLocal?: string): Generator<string> {
  const start = DateTime.fromISO(t.starts_local, { zone: 'UTC' }); // wall clock, zone-free arithmetic
  const r = t.recurrence;
  if (!r) { yield t.starts_local; return; }
  const interval = Math.max(1, Math.floor(r.interval ?? 1));
  const until = r.until ? DateTime.fromISO(r.until, { zone: 'UTC' }).endOf('day') : null;
  const stop = untilLocal ? DateTime.fromISO(untilLocal, { zone: 'UTC' }) : null;
  const count = r.count ?? null;
  let n = 0;
  const emit = (d: DateTime) => { n++; return fmtLocal(d); };
  const done = (d: DateTime) => (until && d > until) || (stop && d > stop) || (count !== null && n >= count) || n >= 5000;
  if (r.freq === 'weekly') {
    const days = [...new Set((r.byweekday?.length ? r.byweekday : [start.weekday]).filter(x => x >= 1 && x <= 7))].sort();
    let week = start.startOf('week');
    for (let i = 0; i < 2000; i++) {
      for (const wd of days) {
        const d = week.plus({ days: wd - 1 }).set({ hour: start.hour, minute: start.minute });
        if (d < start) continue;
        if (done(d)) return;
        yield emit(d);
      }
      week = week.plus({ weeks: interval });
    }
    return;
  }
  for (let i = 0; i < 20000; i++) {
    let d: DateTime;
    if (r.freq === 'daily') d = start.plus({ days: i * interval });
    else if (r.freq === 'monthly') {
      d = start.plus({ months: i * interval });
      if (d.day !== start.day) continue; // e.g. the 31st in a 30-day month: skipped, not moved
    } else {
      d = start.plus({ years: i * interval });
      if (d.day !== start.day || d.month !== start.month) continue; // 29 Feb
    }
    if (done(d)) return;
    yield emit(d);
  }
}

/**
 * Occurrences overlapping [from, to) (instants). Overrides move/rename single occurrences or cancel them.
 * DST-safe: each local start is converted with the series time zone.
 */
export function expandOccurrences(t: SeriesTiming, overrides: OccurrenceOverride[], from: Date, to: Date): Occurrence[] {
  const zone = t.timezone || CALENDAR_TZ;
  const dur = wallMinutes(t.starts_local, t.ends_local);
  const byKey = new Map(overrides.map(o => [o.occurrence, o]));
  const out: Occurrence[] = [];
  // Stop generating once local starts are well past the range (+1 day covers zone offsets; moved overrides handled below).
  const stopLocal = fmtLocal(DateTime.fromJSDate(to).setZone(zone).plus({ days: 1 }));
  const toInst = (local: string) => parseLocal(local, zone).toUTC().toISO()!;
  const maxMove = overrides.reduce((m, o) => (o.starts_local ? Math.max(m, Math.abs(wallMinutes(o.occurrence, o.starts_local))) : m), 0);
  const fromLocalMin = fmtLocal(DateTime.fromJSDate(from).setZone(zone).minus({ minutes: dur + maxMove + 1440 }));
  const stopWithMoves = fmtLocal(DateTime.fromISO(stopLocal, { zone: 'UTC' }).plus({ minutes: maxMove }));
  for (const key of seriesStarts(t, stopWithMoves)) {
    if (key < fromLocalMin) continue;
    const o = byKey.get(key);
    if (o?.cancelled) continue;
    const sLocal = o?.starts_local ?? key;
    const eLocal = o?.ends_local ?? addWall(sLocal, dur);
    const start = toInst(sLocal); const end = toInst(eLocal);
    if (Date.parse(end) <= from.getTime() || Date.parse(start) >= to.getTime()) continue;
    out.push({ key, starts_local: sLocal, ends_local: eLocal, start, end, ...(o ? { override: o } : {}) });
    if (out.length >= CALENDAR_LIMITS.occurrences) break;
  }
  return out.sort((a, b) => a.start.localeCompare(b.start));
}

/** Is `key` a real occurrence of the series (used to validate "this occurrence" edits)? */
export function isOccurrence(t: SeriesTiming, key: string): boolean {
  for (const s of seriesStarts(t, key)) if (s === key) return true;
  return false;
}

/** The recurrence ending the day before `key` (for "this and following" edits). */
export function truncateBefore(r: CalendarRecurrence, key: string): CalendarRecurrence {
  const dayBefore = DateTime.fromISO(key, { zone: 'UTC' }).minus({ days: 1 }).toISODate()!;
  const { count: _c, ...rest } = r;
  return { ...rest, until: r.until && r.until < dayBefore ? r.until : dayBefore };
}

/** Recurrence for the new series created by "this and following": counts become remaining counts. */
export function remainderFrom(t: SeriesTiming, key: string): CalendarRecurrence | null {
  if (!t.recurrence) return null;
  if (!t.recurrence.count) return { ...t.recurrence };
  let before = 0;
  for (const s of seriesStarts(t, key)) { if (s === key) break; before++; }
  return { ...t.recurrence, count: Math.max(1, t.recurrence.count - before) };
}

export function validateRecurrence(r: CalendarRecurrence | null | undefined, startsLocal: string): string | null {
  if (!r) return null;
  if (!['daily', 'weekly', 'monthly', 'yearly'].includes(r.freq)) return 'Unknown repeat frequency';
  const i = r.interval ?? 1;
  if (!Number.isInteger(i) || i < 1 || i > 99) return 'Repeat interval must be 1-99';
  if (r.byweekday && (!Array.isArray(r.byweekday) || r.byweekday.some(d => !Number.isInteger(d) || d < 1 || d > 7))) return 'Invalid weekdays';
  if (r.until) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(r.until)) return 'Invalid end date';
    if (r.until < startsLocal.slice(0, 10)) return 'Repeat end date is before the first event';
    if (DateTime.fromISO(r.until).diff(DateTime.fromISO(startsLocal.slice(0, 10)), 'years').years > 5) return 'Repeats can run for at most 5 years';
  }
  if (r.count != null && (!Number.isInteger(r.count) || r.count < 1 || r.count > 730)) return 'Number of repeats must be 1-730';
  return null;
}

const WD = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export function describeRecurrence(r: CalendarRecurrence | null | undefined, startsLocal?: string): string {
  if (!r) return 'Does not repeat';
  const i = r.interval ?? 1;
  const unit = { daily: 'day', weekly: 'week', monthly: 'month', yearly: 'year' }[r.freq];
  let s = i === 1 ? `Every ${unit}` : `Every ${i} ${unit}s`;
  if (r.freq === 'weekly') {
    const days = r.byweekday?.length ? r.byweekday : startsLocal ? [DateTime.fromISO(startsLocal).weekday] : [];
    if (days.length) s += ` on ${[...days].sort().map(d => WD[d]).join(', ')}`;
  }
  if (r.freq === 'monthly' && startsLocal) s += ` on the ${ordinal(DateTime.fromISO(startsLocal).day)}`;
  if (r.until) s += ` until ${DateTime.fromISO(r.until).toFormat('d LLL yyyy')}`;
  else if (r.count) s += `, ${r.count} times`;
  return s;
}
const ordinal = (n: number) => `${n}${n % 10 === 1 && n !== 11 ? 'st' : n % 10 === 2 && n !== 12 ? 'nd' : n % 10 === 3 && n !== 13 ? 'rd' : 'th'}`;

/** Validates reminder offsets (minutes before start). */
export function normaliseReminders(raw: unknown): { minutes: number[]; error: string | null } {
  if (!Array.isArray(raw)) return { minutes: [], error: 'Reminders must be a list of minutes' };
  const m = [...new Set(raw.map(Number))].sort((a, b) => a - b);
  if (m.some(x => !Number.isInteger(x) || x < 0 || x > CALENDAR_LIMITS.reminderMaxMinutes)) return { minutes: [], error: 'Reminders can be 0 minutes to 7 days before' };
  if (m.length > CALENDAR_LIMITS.reminders) return { minutes: [], error: `At most ${CALENDAR_LIMITS.reminders} reminders per event` };
  return { minutes: m, error: null };
}

export function describeReminder(min: number, allDay = false): string {
  if (allDay && min > 0 && min % 1440 !== 0 && min < 1440 * 7) {
    // all-day events start at 00:00 local: 900 = "day before at 09:00"
    const days = Math.ceil(min / 1440); const at = days * 1440 - min;
    return `${days === 1 ? 'Day' : `${days} days`} before at ${String(Math.floor(at / 60)).padStart(2, '0')}:${String(at % 60).padStart(2, '0')}`;
  }
  if (min === 0) return 'At start time';
  if (min % 1440 === 0) return `${min / 1440} day${min === 1440 ? '' : 's'} before`;
  if (min % 60 === 0) return `${min / 60} hour${min === 60 ? '' : 's'} before`;
  return `${min} minutes before`;
}

/** Reminder fire times of one occurrence: due when fire_at <= now and the occurrence has not started. */
export function dueReminders(occStartIso: string, minutes: number[], now: Date): number[] {
  const start = Date.parse(occStartIso);
  if (start <= now.getTime()) return [];
  return minutes.filter(m => start - m * 60_000 <= now.getTime());
}

export interface CalendarItem {
  /** unique per rendered item */
  id: string;
  layer: CalendarLayer;
  title: string;
  /** timed: ISO instants; all-day: local dates (start inclusive, end exclusive) */
  start: string;
  end: string;
  all_day: boolean;
  color: string;
  subtitle?: string | null;
  link?: string | null;
  event_id?: string;
  occurrence?: string;
  recurring?: boolean;
  my_rsvp?: Rsvp | null;
  /** events: I accepted. Attending events show even when their layer is filtered out. */
  attending?: boolean;
  /** events: accepted / tentative counts and the first few accepted attendees (same visibility as the event itself) */
  going_count?: number;
  maybe_count?: number;
  attendees_preview?: Array<{ user_id: string; display_name: string }>;
  /** People's messages in the event's discussion (#49). */
  message_count?: number;
  user_id?: string;
  status?: string;
  location_text?: string | null;
}

export interface CalendarEventView {
  id: string;
  workspace_id: string;
  scope: CalendarScope;
  team_id: string | null;
  team_name?: string | null;
  location_id: string | null;
  location_name?: string | null;
  /** t701u: the building's short code (feed cards show it top right). */
  location_code?: string | null;
  title: string;
  description: string | null;
  location_text: string | null;
  all_day: boolean;
  starts_local: string;
  ends_local: string;
  timezone: string;
  recurrence: CalendarRecurrence | null;
  conversation_id: string | null;
  conversation_name?: string | null;
  task_id: string | null;
  task_label?: string | null;
  task_link?: string | null;
  created_by: string;
  created_by_name: string;
  series_parent_id: string | null;
  /** t360u: job (position, else job title) and primary building, as the directory shows anyone. */
  attendees: Array<{ user_id: string; display_name: string; rsvp: Rsvp; job?: string | null; building?: string | null }>;
  my_rsvp: Rsvp | null;
  /** null = my defaults apply */
  my_reminders: number[] | null;
  effective_reminders: number[];
  can_edit: boolean;
  overrides: OccurrenceOverride[];
}

export interface CalendarFilters {
  layers: CalendarLayer[];
  tasks: TaskScope;
  location_id?: string | null;
  team_id?: string | null;
  view?: 'month' | 'week' | 'day' | 'agenda';
}
export const DEFAULT_CALENDAR_FILTERS: CalendarFilters = { layers: ['company', 'team', 'building', 'personal', 'birthdays', 'tasks', 'absences', 'closures'], tasks: 'mine', view: 'month' };

export interface CalendarSettings {
  default_reminders_timed: number[];
  default_reminders_all_day: number[];
  filters: CalendarFilters;
  presets: Array<{ name: string; filters: CalendarFilters }>;
  ical_enabled: boolean;
}

// ---- iCalendar (RFC 5545) -----------------------------------------------------------------------------------
const icsEscape = (s: string) => s.replace(/\\/g, '\\\\').replace(/\r?\n/g, '\\n').replace(/([,;])/g, '\\$1');
const icsUtc = (iso: string) => DateTime.fromISO(iso, { zone: 'utc' }).toFormat("yyyyLLdd'T'HHmmss'Z'");
const icsDate = (d: string) => d.replace(/-/g, '').slice(0, 8);
/** Lines longer than 75 octets are folded (simplified: by characters). */
const fold = (line: string) => line.length <= 74 ? line : line.match(/.{1,73}/g)!.map((l, i) => (i ? ' ' + l : l)).join('\r\n');

export function toIcs(name: string, items: Array<CalendarItem & { description?: string | null; url?: string | null }>, now = new Date()): string {
  const stamp = icsUtc(now.toISOString());
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//The Hub//The Hub Calendar//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', `X-WR-CALNAME:${icsEscape(name)}`, 'X-WR-TIMEZONE:Europe/London'];
  for (const it of items) {
    lines.push('BEGIN:VEVENT', `UID:${it.id.replace(/[^\w.-]/g, '-')}@collab.tradeoutlet`, `DTSTAMP:${stamp}`);
    if (it.all_day) lines.push(`DTSTART;VALUE=DATE:${icsDate(it.start)}`, `DTEND;VALUE=DATE:${icsDate(it.end)}`);
    else lines.push(`DTSTART:${icsUtc(it.start)}`, `DTEND:${icsUtc(it.end)}`);
    lines.push(`SUMMARY:${icsEscape(it.title)}`);
    if (it.location_text) lines.push(`LOCATION:${icsEscape(it.location_text)}`);
    const desc = [it.subtitle, it.description].filter(Boolean).join('\n');
    if (desc) lines.push(`DESCRIPTION:${icsEscape(desc)}`);
    if (it.url) lines.push(`URL:${it.url}`);
    lines.push(`CATEGORIES:${icsEscape(CALENDAR_LAYER_LABELS[it.layer])}`, 'END:VEVENT');
  }
  lines.push('END:VCALENDAR');
  return lines.map(fold).join('\r\n') + '\r\n';
}

/** "Thu 15 Oct, 09:00-10:30" / "Thu 15 Oct (all day)" in UK time. */
export function formatEventWhen(startLocal: string, endLocal: string, allDay: boolean): string {
  const s = DateTime.fromISO(startLocal); const e = DateTime.fromISO(endLocal);
  if (allDay) {
    const last = e.minus({ days: 1 });
    return last.hasSame(s, 'day') ? `${s.toFormat('ccc d LLL yyyy')} (all day)` : `${s.toFormat('ccc d LLL')} - ${last.toFormat('ccc d LLL yyyy')} (all day)`;
  }
  return s.hasSame(e, 'day') ? `${s.toFormat('ccc d LLL yyyy, HH:mm')}-${e.toFormat('HH:mm')}` : `${s.toFormat('ccc d LLL, HH:mm')} - ${e.toFormat('ccc d LLL yyyy, HH:mm')}`;
}

/** Client-side layer filter: attending events stay visible whatever the layer toggles say. */
export function visibleCalendarItems(items: CalendarItem[], layers: CalendarLayer[]): CalendarItem[] {
  return items.filter(i => i.attending || layers.includes(i.layer));
}
